import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton'
import { useCard, useCards } from '../../hooks/useApi'
import { CALCULATOR_RECIPES } from '../../constants/calculatorAccess'
import { calculateCoefficient, roundAmount } from '../../utils/calculator'
import {
  parseIngredientsFromContent,
  parseRecipeMetaFromContent,
} from '../../utils/parseIngredients'
import RecipePicker from './components/RecipePicker'
import ShapeFieldset from './components/ShapeFieldset'
import {
  EMPTY_SHAPE,
  describeShape,
  fromRecipeMeta,
  isShapeFilled,
  toHeight,
  toShape,
} from './shapeValue'
import './ShapeCalculatorPage.css'

/**
 * Пересчёт рецепта под другую форму: по объёму исходной и новой формы считает
 * коэффициент, на который нужно умножить ингредиенты.
 *
 * Исходные размеры можно подставить из рецепта — списком идут те же карточки,
 * для которых доступен калькулятор внутри рецепта. Ручной ввод остаётся: он
 * покрывает рецепты не из приложения.
 */
const ShapeCalculatorPage = () => {
  const [isPickerOpen, setPickerOpen] = useState(false)

  // Пока открыт список рецептов, системная кнопка «Назад» закрывает его.
  // Иначе человек, листающий список, одним нажатием улетал бы с калькулятора.
  const closePicker = useCallback(() => setPickerOpen(false), [])
  useTelegramBackButton(true, isPickerOpen ? closePicker : undefined)

  const [recipeId, setRecipeId] = useState<number | null>(null)
  const [original, setOriginal] = useState(EMPTY_SHAPE)
  const [target, setTarget] = useState(EMPTY_SHAPE)
  const [weight, setWeight] = useState('')

  // Названия берём из списка рецептов, а живое с бэка подставляем поверх, если
  // карточка есть в загруженных: у аккаунта без доступа список карточек может
  // не содержать эти id, и выбор рецепта остался бы пустым.
  const { data: cardsData } = useCards()
  const recipeOptions = useMemo(() => {
    const titleById = new Map(
      (cardsData?.cards ?? []).map(card => [card.card_id, card.title])
    )
    const iconById = new Map(
      (cardsData?.cards ?? []).map(card => [card.card_id, card.icon_url])
    )
    return CALCULATOR_RECIPES.map(recipe => ({
      cardId: recipe.cardId,
      title: titleById.get(recipe.cardId) ?? recipe.title,
      icon: iconById.get(recipe.cardId) ?? '',
    })).sort((a, b) => a.title.localeCompare(b.title, 'ru'))
  }, [cardsData])

  const {
    data: recipeData,
    isLoading: isRecipeLoading,
    isError: isRecipeError,
  } = useCard(recipeId)
  const recipeContent = recipeData?.card?.content

  // Чего в рецепте не нашлось. Общее «не указаны форма и высота» не помогало:
  // человек не понимал, что именно вписывать руками.
  const [missing, setMissing] = useState<string[]>([])
  // Размеры подставились из рецепта — правки закрыты. Менять их незачем:
  // исходная форма это свойство рецепта, а не пожелание пользователя. Нужны
  // другие размеры — выбирается «Свои размеры» в списке рецептов.
  const [isFromRecipe, setFromRecipe] = useState(false)

  // Подставляем размеры, когда долетел контент выбранного рецепта. Зависимость
  // от самого контента, а не от разобранной меты: объект меты пересоздавался бы
  // на каждый рендер и затирал бы ручные правки.
  useEffect(() => {
    if (recipeId === null) {
      setMissing([])
      setFromRecipe(false)
      return
    }
    if (!recipeContent) return

    const meta = parseRecipeMetaFromContent(recipeContent)
    const shape = fromRecipeMeta(meta)
    setOriginal(shape)
    setWeight(meta.originalWeight ? String(meta.originalWeight) : '')

    const gaps: string[] = []
    if (!meta.originalShape) gaps.push('форма')
    if (meta.originalHeight === null) gaps.push('высота')
    if (meta.originalWeight === null) gaps.push('вес')
    setMissing(gaps)

    // Всё подставилось — поля исходной формы закрываем от правок.
    setFromRecipe(isShapeFilled(shape))
  }, [recipeId, recipeContent])

  // Состав рецепта — чтобы показать пересчитанные граммовки, а не только
  // коэффициент: из рецепта калькулятор уже так умеет, здесь было иначе.
  const sections = useMemo(
    () => (recipeContent ? parseIngredientsFromContent(recipeContent) : []),
    [recipeContent]
  )

  const isReady = isShapeFilled(original) && isShapeFilled(target)

  const coefficient = useMemo(() => {
    const originalShape = toShape(original)
    const targetShape = toShape(target)
    if (!originalShape || !targetShape) return 0
    return calculateCoefficient(
      originalShape,
      toHeight(original),
      targetShape,
      toHeight(target)
    )
  }, [original, target])

  const originalWeight = Number(weight) || 0

  // Карточка может не отдать текст: нет доступа или запрос упал.
  const isRecipeUnavailable =
    recipeId !== null && !isRecipeLoading && (isRecipeError || !recipeContent)

  const handleReset = () => {
    setRecipeId(null)
    setFromRecipe(false)
    setMissing([])
    setOriginal(EMPTY_SHAPE)
    setTarget(EMPTY_SHAPE)
    setWeight('')
  }

  // safe-area-inset на корень не вешаем: шелл уже оборачивает роутинг в этот
  // класс, а повторный перебил бы наш padding-bottom нулём.
  return (
    <div className="shape-calc-page">
      <div className="shape-calc-body">
        <h1 className="shape-calc-title">Калькулятор формы</h1>
        <p className="shape-calc-subtitle">
          Укажите форму из рецепта и форму, в которой печёте, — посчитаем
          коэффициент, на который нужно умножить все ингредиенты.
        </p>

        <RecipePicker
          options={recipeOptions}
          value={recipeId}
          onChange={setRecipeId}
          isOpen={isPickerOpen}
          onOpenChange={setPickerOpen}
        />

        {isRecipeLoading && <p className="shape-calc-note">Загружаем рецепт…</p>}

        {isRecipeUnavailable && (
          <p className="shape-calc-note warning">
            Не удалось получить текст рецепта — заполните размеры вручную.
          </p>
        )}

        {!isRecipeLoading && missing.length > 0 && (
          <p className="shape-calc-note warning">
            В рецепте не указано: {missing.join(', ')}. Впишите вручную —
            посчитаем так же.
          </p>
        )}

        {isFromRecipe ? (
          <div className="shape-calc-original-fixed">
            <span className="shape-calc-original-fixed__label">
              Форма из рецепта
            </span>
            <strong>{describeShape(original)}</strong>
          </div>
        ) : (
          <ShapeFieldset
            title="Форма из рецепта"
            value={original}
            onChange={setOriginal}
          />
        )}

        <ShapeFieldset title="Ваша форма" value={target} onChange={setTarget} />

        <label className="shape-calc-weight">
          <span>Вес рецепта, г (необязательно)</span>
          <input
            type="number"
            inputMode="decimal"
            value={weight}
            onChange={e => setWeight(e.target.value)}
            placeholder="1200"
          />
        </label>

        {isReady ? (
          <div className="shape-calc-result">
            <div className="shape-calc-coefficient">
              Коэффициент <strong>×{coefficient.toFixed(2)}</strong>
            </div>

            <p className="shape-calc-result-text">
              Умножьте на него количество каждого ингредиента.
            </p>

            {originalWeight > 0 && (
              <div className="shape-calc-weight-result">
                Примерный вес:{' '}
                <strong>~{Math.round(originalWeight * coefficient)} г</strong>
                <span> (исходный: {originalWeight} г)</span>
              </div>
            )}

            <dl className="shape-calc-summary">
              <div>
                <dt>Из рецепта</dt>
                <dd>{describeShape(original)}</dd>
              </div>
              <div>
                <dt>Ваша</dt>
                <dd>{describeShape(target)}</dd>
              </div>
            </dl>

            {sections.length > 0 && (
              <div className="shape-calc-ingredients">
                {sections.map(section => (
                  <div key={section.name} className="shape-calc-section">
                    <h3>
                      {section.name}
                      {!section.scalable && <span>без пересчёта</span>}
                    </h3>
                    {section.ingredients.map((ing, index) => {
                      const scaled =
                        section.scalable && ing.amount !== null
                          ? ing.amount * coefficient
                          : null
                      return (
                        <div key={index} className="shape-calc-ingredient">
                          <span>{ing.name}</span>
                          {scaled !== null ? (
                            <span>
                              <s>
                                {ing.amount} {ing.unit}
                              </s>{' '}
                              <strong>
                                {roundAmount(scaled)} {ing.unit}
                              </strong>
                            </span>
                          ) : (
                            <span>{ing.unit || '—'}</span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ))}
              </div>
            )}

            <p className="shape-calc-hint">
              Для крема и ганаша добавьте +5–10% к рассчитанному количеству,
              особенно для прямоугольных форм.
            </p>

            <button
              type="button"
              className="shape-calc-reset"
              onClick={handleReset}
            >
              Очистить
            </button>
          </div>
        ) : (
          <p className="shape-calc-placeholder">
            Заполните размеры и высоту обеих форм — расчёт появится здесь.
          </p>
        )}
      </div>
    </div>
  )
}

export default ShapeCalculatorPage
